// src/web-app/index.js
// Web app entry point - Firebase hosted, no Tauri, no database
import { initializeAuth, getAuthReadyPromise, getAuthClientId, getAccessToken } from './auth/auth-handler.js';
import { showUserInfo, showAuthForm } from './auth/auth-ui.js';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from 'firebase/auth';
import { resolveApiBase } from '../common/api.js';
import { FootnotesManager } from '../common/footnotes.js';
import { setupFileUpload } from './handlers/file-upload.js';
import { setupPdfParsing, applyPdfParseResponse } from './handlers/pdf-parsing.js';
import { renderPages } from '../common/ui.js';
import { uploadHtmlReport, getAuthInstance, isZdrRestrictedUser } from './lib/auth/firebase.js';
import { settingsManager } from '../common/settings.js';
import { fetchCreditBalance } from '../common/credits.js';
import { initCreditDisplay } from '../common/credits.js';
import { initUsageModal } from '../common/usage-summary.js';
import { initializeQueueUI } from '../common/ui/queue-manager.js';
import { quoteIssueLabel } from '../common/ui/badges-status.js';
import { DocumentsStore } from './documents/documents-store.js';
import { initializeDashboard } from './documents/dashboard.js';
import './web-app.css';

const byId = (id) => document.getElementById(id);
const SIDEBAR_COLLAPSE_KEY = 'fc.web.sidebarCollapsed';

function formatCents(cents) {
  return `$${((Number(cents) || 0) / 100).toFixed(2)}`;
}

const formatPrice = (amountCents, code) => new Intl.NumberFormat('en', {
  style: 'currency',
  currency: String(code || 'usd').toUpperCase(),
}).format((Number(amountCents) || 0) / 100);

// #status is written by every flow (parsing, dashboard, reports) and many of
// them reset it to "Idle". Hide the line whenever there is nothing to say.
function setupStatusLine() {
  const statusEl = byId('status');
  if (!statusEl) return;
  const sync = () => {
    const text = statusEl.textContent.trim();
    statusEl.classList.toggle('is-idle', !text || text === 'Idle');
  };
  new MutationObserver(sync).observe(statusEl, { childList: true, characterData: true, subtree: true });
  sync();
}

function setupWebPanes() {
  const tabs = Array.from(document.querySelectorAll('[data-web-pane-target]'));
  const panes = Array.from(document.querySelectorAll('[data-web-pane]'));
  if (!tabs.length || !panes.length) return;

  const showPane = (paneName, { updateHash = true } = {}) => {
    // Old #password links land on the Account pane's password section.
    const scrollToPassword = paneName === 'password';
    const target = scrollToPassword ? 'account' : (paneName || 'app');
    panes.forEach((pane) => {
      pane.classList.toggle('active', pane.dataset.webPane === target);
    });
    document.querySelectorAll('.web-pane-tab').forEach((tab) => {
      tab.classList.toggle('active', tab.dataset.webPaneTarget === target);
    });
    document.body.classList.toggle('is-account-pane', target !== 'app');
    if (updateHash) {
      const nextHash = target === 'app' ? '' : `#${target}`;
      if (window.location.hash !== nextHash) {
        window.history.replaceState({}, document.title, `${window.location.pathname}${window.location.search}${nextHash}`);
      }
    }
    if (target === 'credits' && typeof window.refreshAccountPaneCredits === 'function') {
      window.refreshAccountPaneCredits();
    }
    if (scrollToPassword) {
      byId('account-password-section')?.scrollIntoView({ block: 'start' });
    }
  };

  tabs.forEach((tab) => {
    tab.addEventListener('click', (event) => {
      const paneName = tab.dataset.webPaneTarget;
      if (!paneName) return;
      event.preventDefault();
      showPane(paneName);
    });
  });

  window.showWebPane = showPane;
  const initial = window.location.hash?.slice(1);
  if (['account', 'credits', 'password'].includes(initial)) {
    showPane(initial, { updateHash: false });
  }
}

function updateAccountProfilePane() {
  const user = getAuthInstance()?.currentUser;
  const email = user?.email || 'Not signed in';
  const displayName = user?.displayName || (user?.email ? user.email.split('@')[0] : 'Not set');
  const created = user?.metadata?.creationTime
    ? new Date(user.metadata.creationTime).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
    : 'Unknown';
  const clientId = getAuthClientId?.() || 'Unknown';

  if (byId('account-profile-email')) byId('account-profile-email').textContent = email;
  if (byId('account-profile-name')) byId('account-profile-name').textContent = displayName;
  if (byId('account-created-at')) byId('account-created-at').textContent = created;
  if (byId('account-client-id')) byId('account-client-id').textContent = clientId;
}

async function fetchUsageSummary(apiBase, token) {
  const end = new Date();
  const start = new Date(end.getTime() - 30 * 24 * 60 * 60 * 1000);
  const params = new URLSearchParams({
    start_date: start.toISOString().slice(0, 10),
    end_date: end.toISOString().slice(0, 10),
  });
  const response = await fetch(`${apiBase}/usage/summary?${params.toString()}`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!response.ok) throw new Error(`Usage request failed (${response.status})`);
  return response.json();
}

function renderAccountCredits(credits) {
  const remaining = credits?.remaining_cents ?? credits?.balance_cents ?? 0;
  const low = !!credits?.is_warning || !!credits?.is_blocked;
  const lowText = credits?.is_blocked
    ? 'Your credits are used up. Add credits to verify more documents.'
    : 'Your credit balance is running low.';

  const balanceEl = byId('account-credit-balance');
  if (balanceEl) {
    balanceEl.textContent = formatCents(remaining);
    balanceEl.classList.toggle('is-low', low);
  }

  const message = byId('account-credit-message');
  if (message) {
    message.textContent = low ? lowText : '';
    message.classList.toggle('warning', low);
  }

  // Same warning on the Documents page, where the money is actually spent.
  const banner = byId('dashboard-credit-banner');
  if (banner) {
    banner.hidden = !low;
    const bannerText = byId('dashboard-credit-banner-text');
    if (bannerText) bannerText.textContent = `${lowText} Balance: ${formatCents(remaining)}.`;
  }
}

function renderAccountUsage(usage) {
  const total = usage?.total_cost_cents || 0;
  const byType = usage?.by_operation_type || {};
  if (byId('account-usage-summary')) {
    byId('account-usage-summary').innerHTML = `<span>Total used</span><strong>${formatCents(total)}</strong>`;
  }
  if (byId('account-usage-llm')) byId('account-usage-llm').textContent = formatCents(byType.llm?.cost_cents || 0);
  if (byId('account-usage-embedding')) byId('account-usage-embedding').textContent = formatCents(byType.embedding?.cost_cents || 0);
  if (byId('account-usage-search')) byId('account-usage-search').textContent = formatCents(byType.web_search?.cost_cents || 0);
}

async function fetchPurchases(apiBase, token) {
  const response = await fetch(`${apiBase}/billing/purchases`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!response.ok) throw new Error(`Purchases request failed (${response.status})`);
  return (await response.json())?.purchases || [];
}

function renderPurchases(purchases, apiBase) {
  const container = byId('account-purchases');
  if (!container) return;
  container.replaceChildren();
  if (!purchases.length) {
    container.innerHTML = '<span class="account-muted">No purchases yet.</span>';
    return;
  }
  for (const purchase of purchases) {
    const row = document.createElement('div');
    row.className = 'account-purchase-row';

    const when = document.createElement('span');
    when.className = 'account-purchase-date';
    const date = purchase.created_at ? new Date(purchase.created_at) : null;
    when.textContent = date && !Number.isNaN(date.getTime())
      ? date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
      : '';

    const what = document.createElement('span');
    what.className = 'account-purchase-what';
    const paid = purchase.amount_total != null && purchase.currency
      ? formatPrice(purchase.amount_total, purchase.currency)
      : null;
    what.textContent = paid
      ? `${formatCents(purchase.credit_cents)} credit · paid ${paid}`
      : `${formatCents(purchase.credit_cents)} credit`;

    const receipt = document.createElement('span');
    receipt.className = 'account-purchase-receipt';
    if (purchase.has_receipt && purchase.id) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'btn-text btn-small';
      button.textContent = 'Receipt';
      button.addEventListener('click', () => openReceipt(apiBase, purchase.id, button));
      receipt.appendChild(button);
    }

    row.append(when, what, receipt);
    container.appendChild(row);
  }
}

// Receipt URLs come from Stripe on demand (they are not stored). Open the tab
// synchronously so the popup blocker treats it as part of the click.
async function openReceipt(apiBase, sessionId, button) {
  const popup = window.open('about:blank', '_blank');
  if (popup) popup.opener = null;
  button.disabled = true;
  try {
    const token = await getAccessToken();
    const response = await fetch(`${apiBase}/billing/purchases/${encodeURIComponent(sessionId)}/receipt`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.receipt_url) {
      throw new Error(errorDetailMessage(data.detail, 'Receipt is not available.'));
    }
    if (popup) popup.location.replace(data.receipt_url);
    else window.open(data.receipt_url, '_blank', 'noopener');
  } catch (err) {
    popup?.close();
    const status = byId('account-purchase-status');
    if (status) {
      status.textContent = err.message || 'Receipt is not available.';
      status.classList.add('warning');
    }
  } finally {
    button.disabled = false;
  }
}

function errorDetailMessage(detail, fallback) {
  if (typeof detail === 'string') return detail;
  return detail?.message || fallback;
}

// Buy-credits card: lists packs from /billing/credit-packs and redirects to Stripe Checkout.
// Stripe returns the user to ?billing=success|cancelled; the webhook grants credits asynchronously.
function setupCreditPurchase(apiBase, refreshCredits, signal) {
  const packList = byId('account-credit-packs');
  if (!packList) return;
  const statusEl = byId('account-purchase-status');

  const setStatus = (text, isWarning = false) => {
    if (!statusEl) return;
    statusEl.textContent = text;
    statusEl.classList.toggle('warning', isWarning);
  };

  // Credits are always USD; the currency only changes what the card is charged.
  let catalog = null;
  let currency = 'usd';

  const renderCurrencySwitch = (currencies) => {
    const switchEl = byId('account-credit-currency');
    if (!switchEl) return;
    switchEl.replaceChildren();
    if (currencies.length < 2) {
      switchEl.hidden = true;
      return;
    }
    switchEl.hidden = false;
    for (const code of currencies) {
      const option = document.createElement('button');
      option.type = 'button';
      option.className = code === currency ? 'btn-primary btn-small' : 'btn-secondary btn-small';
      option.dataset.currency = code;
      option.textContent = code.toUpperCase();
      option.addEventListener('click', () => {
        currency = code;
        renderCurrencySwitch(currencies);
        renderPackButtons();
      }, { signal });
      switchEl.appendChild(option);
    }
  };

  const renderPackButtons = () => {
    packList.replaceChildren();
    if (!catalog) return;
    for (const pack of catalog.packs) {
      const price = pack.prices.find((p) => p.currency === currency);
      if (!price) continue;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = pack.featured ? 'btn-primary btn-small' : 'btn-secondary btn-small';
      button.dataset.packId = pack.id;
      button.dataset.enabled = String(!!price.checkout_enabled);
      button.disabled = !price.checkout_enabled;
      // Only spell out the credit when it differs from the price (EUR packs, bonus packs).
      const priceText = formatPrice(price.amount_cents, currency);
      const creditText = formatCents(pack.credit_cents);
      button.textContent = currency === 'usd' && price.amount_cents === pack.credit_cents
        ? `${pack.name} — ${priceText}`
        : `${pack.name} — ${priceText} (${creditText} credit)`;
      button.addEventListener('click', () => startCheckout(pack.id), { signal });
      packList.appendChild(button);
    }
  };

  const renderPacks = (data) => {
    catalog = data;
    const currencies = data.currencies || [];
    if (!data.stripe_ready || currencies.length === 0) {
      packList.replaceChildren();
      renderCurrencySwitch([]);
      setStatus('Card payments are not available yet.');
      return;
    }
    if (!currencies.includes(currency)) currency = currencies[0];
    renderCurrencySwitch(currencies);
    renderPackButtons();
  };

  const startCheckout = async (packId) => {
    const token = await getAccessToken();
    if (!token) return;
    const buttons = Array.from(packList.querySelectorAll('button'));
    buttons.forEach((button) => { button.disabled = true; });
    setStatus('Redirecting to secure checkout...');

    const returnUrl = (result) => `${window.location.origin}${window.location.pathname}?billing=${result}#credits`;
    try {
      const response = await fetch(`${apiBase}/billing/checkout-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          pack_id: packId,
          currency,
          success_url: returnUrl('success'),
          cancel_url: returnUrl('cancelled'),
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(errorDetailMessage(data.detail, `Checkout failed (${response.status})`));
      }
      window.location.assign(data.url);
    } catch (err) {
      console.warn('[Billing] Checkout failed:', err);
      setStatus(err.message || 'Checkout failed.', true);
      buttons.forEach((button) => { button.disabled = button.dataset.enabled !== 'true'; });
    }
  };

  const loadPacks = async () => {
    const token = await getAccessToken();
    if (!token) return;
    try {
      const response = await fetch(`${apiBase}/billing/credit-packs`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`Billing request failed (${response.status})`);
      renderPacks(await response.json());
    } catch (err) {
      console.warn('[Billing] Failed to load credit packs:', err);
      setStatus('Could not load credit packs.', true);
    }
  };

  const handleCheckoutReturn = () => {
    const billing = new URLSearchParams(window.location.search).get('billing');
    if (!billing) return;
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.hash}`);
    if (billing === 'success') {
      setStatus('Payment received. Your credits will appear in a moment.');
      [2000, 6000].forEach((ms) => setTimeout(refreshCredits, ms));
    } else if (billing === 'cancelled') {
      setStatus('Checkout cancelled. You have not been charged.');
    }
  };

  handleCheckoutReturn();
  loadPacks();
}

function setupAccountPanes(apiBase, signal) {
  updateAccountProfilePane();

  const refreshCredits = async () => {
    const token = await getAccessToken();
    if (!token) return;
    const refreshBtn = byId('account-refresh-credits');
    if (refreshBtn) refreshBtn.disabled = true;
    try {
      const [credits, usage, purchases] = await Promise.all([
        fetchCreditBalance(apiBase, token),
        fetchUsageSummary(apiBase, token).catch((err) => {
          console.warn('[AccountPane] Failed to fetch usage:', err);
          return null;
        }),
        fetchPurchases(apiBase, token).catch((err) => {
          console.warn('[AccountPane] Failed to fetch purchases:', err);
          return null;
        }),
      ]);
      renderAccountCredits(credits);
      if (usage) renderAccountUsage(usage);
      if (purchases) renderPurchases(purchases, apiBase);
    } catch (err) {
      console.warn('[AccountPane] Failed to refresh credits:', err);
      const message = byId('account-credit-message');
      if (message) {
        message.textContent = err.message || 'Could not load credits.';
        message.classList.add('warning');
      }
    } finally {
      if (refreshBtn) refreshBtn.disabled = false;
    }
  };

  window.refreshAccountPaneCredits = refreshCredits;
  byId('account-refresh-credits')?.addEventListener('click', refreshCredits, { signal });
  refreshCredits();
  setupCreditPurchase(apiBase, refreshCredits, signal);

  const currentPassword = byId('account-current-password');
  const newPassword = byId('account-new-password');
  const confirmPassword = byId('account-confirm-password');
  const changeButton = byId('account-change-password');
  const passwordStatus = byId('account-password-status');

  const updatePasswordButton = () => {
    if (!changeButton) return;
    changeButton.disabled = !(currentPassword?.value && newPassword?.value && confirmPassword?.value);
  };
  [currentPassword, newPassword, confirmPassword].forEach((input) => {
    input?.addEventListener('input', () => {
      updatePasswordButton();
      if (passwordStatus) passwordStatus.textContent = '';
    }, { signal });
  });

  changeButton?.addEventListener('click', async () => {
    const user = getAuthInstance()?.currentUser;
    if (!user?.email) {
      if (passwordStatus) passwordStatus.textContent = 'Email/password sign-in is required.';
      return;
    }
    if ((newPassword?.value || '').length < 8) {
      if (passwordStatus) passwordStatus.textContent = 'Use at least 8 characters.';
      return;
    }
    if (newPassword.value !== confirmPassword.value) {
      if (passwordStatus) passwordStatus.textContent = 'Passwords do not match.';
      return;
    }
    changeButton.disabled = true;
    if (passwordStatus) passwordStatus.textContent = 'Updating...';
    try {
      const credential = EmailAuthProvider.credential(user.email, currentPassword.value);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword.value);
      currentPassword.value = '';
      newPassword.value = '';
      confirmPassword.value = '';
      if (passwordStatus) passwordStatus.textContent = 'Password updated.';
    } catch (err) {
      console.warn('[AccountPane] Password update failed:', err);
      if (passwordStatus) passwordStatus.textContent = err.message || 'Password update failed.';
    } finally {
      updatePasswordButton();
    }
  }, { signal });
}

// Documents dashboard rollout flag (plan 2A.5): default on; set
// localStorage FC_WEB_DASHBOARD = 'off' to fall back to the legacy queue view.
const isDashboardEnabled = () => {
  try {
    return localStorage.getItem('FC_WEB_DASHBOARD') !== 'off';
  } catch {
    return true;
  }
};

function setupSidebarCollapse() {
  const app = byId('app');
  const toggleBtn = byId('btn-toggle-sidebar');
  const indicatorBtn = byId('sidebar-collapsed-indicator');
  if (!app || !toggleBtn || !indicatorBtn) return;

  const iconEl = toggleBtn.querySelector('.sidebar-toggle-icon');
  const labelEl = toggleBtn.querySelector('.sidebar-toggle-label');

  const applyCollapsedState = (collapsed) => {
    app.classList.toggle('sidebar-collapsed', collapsed);
    toggleBtn.setAttribute('aria-expanded', String(!collapsed));
    indicatorBtn.setAttribute('aria-expanded', String(!collapsed));

    if (iconEl) iconEl.textContent = collapsed ? '›' : '‹';
    if (labelEl) labelEl.textContent = collapsed ? 'Expand' : 'Collapse';
    toggleBtn.title = collapsed ? 'Expand controls panel' : 'Collapse controls panel';
  };

  const saveCollapsedState = (collapsed) => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSE_KEY, collapsed ? '1' : '0');
    } catch {}
  };

  const toggleCollapsedState = () => {
    const collapsed = !app.classList.contains('sidebar-collapsed');
    applyCollapsedState(collapsed);
    saveCollapsedState(collapsed);
  };

  toggleBtn.addEventListener('click', toggleCollapsedState);
  indicatorBtn.addEventListener('click', toggleCollapsedState);

  // Restore persisted state for desktop-sized layouts.
  let initialCollapsed = false;
  try {
    initialCollapsed = localStorage.getItem(SIDEBAR_COLLAPSE_KEY) === '1';
  } catch {}
  applyCollapsedState(initialCollapsed);
}

// Set up citation tabs for filtering by verification status
function setupCitationTabs() {
  const tabs = Array.from(document.querySelectorAll('.citation-tab'));
  const mainList = byId('footnote-list');
  let activeTab = 'all';

  const applyActiveFilter = () => {
    const cards = Array.from(mainList?.querySelectorAll('.card') || []);
    cards.forEach((card) => {
      const category = card.dataset.citationCategory || 'unresolved';
      card.style.display = activeTab === 'all' || category === activeTab ? '' : 'none';
    });
  };
  
  tabs.forEach(tab => {
    tab.addEventListener('click', () => {
      activeTab = tab.dataset.tab || 'all';
      
      // Update active tab
      tabs.forEach(t => t.classList.remove('active'));
      tab.classList.add('active');
      applyActiveFilter();
    });
  });

  window.updateCitationTabs = markCitationCardCategory;
  window.clearCitationTabs = resetCitationTabs;
  window.updateTabCounts = updateTabCounts;
  window.applyCitationTabFilter = () => {
    updateTabCounts();
    applyActiveFilter();
  };
}

// Categorize a citation result into verified/issues/unresolved
// Returns null if no valid final label is present (still processing)
function categorizeCitation(result) {
  if (!result) return null; // No result yet = don't categorize
  
  // Extract label from all possible nested locations
  const rawLabel = result?.label || 
                   result?.analyze_result?.label || 
                   result?.analyze_result?.congruence_result?.label ||
                   result?.congruence_result?.label || '';
  
  const label = String(rawLabel).toLowerCase().replace(/-/g, '_');
  const status = String(result?.status || '').toLowerCase();
  
  // Skip if no label or still processing
  if (!label || ['pending', 'processing', 'resolving', 'analyzing', ''].includes(label)) {
    return null;
  }
  
  // Verified: supports, partial, quote-exact, quote-close (successful verification)
  // — unless the quote itself is a confirmed misquote, which is an Issue regardless of
  // whether the source supports the claim.
  if (['supports', 'partial', 'weak/qualified', 'quote_exact', 'quote_close'].includes(label)) {
    return quoteIssueLabel(result) ? 'issues' : 'verified';
  }
  
  // Issues: contradicts, wrong_document, hallucination, quote-mismatch, unrelated (verification found problems)
  if ([
    'hallucination', 'wrong_document', 'refutes', 'disputes', 'contradicts', 'unsupported', 'neutral',
    'quote_mismatch', 'quote_not_found', 'unrelated', 'mismatch'
  ].includes(label)) {
    return 'issues';
  }
  
  // Unresolved: All resolution failures and incomplete states
  // This includes: blocked, paywalled, access_denied, bot_protected, not_found, 
  // timeout, network_error, vague_citation, no_search_results, ambiguous,
  // insufficient, error, wet, comment, unclear, mixed
  return 'unresolved';
}

// Mark one rendered card with a final category. The tab UI filters this single
// list instead of cloning cards into separate DOM trees.
function markCitationCardCategory(footnoteId, citationId, result) {
  const mainList = byId('footnote-list');
  if (!mainList) return;
  
  // Find the card in the main list
  const card = mainList.querySelector(`.card[data-id="${footnoteId}"]`);
  if (!card) return;
  
  const category = categorizeCitation(result);
  
  // If no valid category (still processing), don't update tabs yet
  if (!category) {
    console.log(`[CitationTabs] Skipping ${footnoteId}/${citationId} - no final label yet`);
    return;
  }
  
  console.log(`[CitationTabs] Categorizing ${footnoteId}/${citationId} as: ${category}`);
  card.dataset.citationCategory = category;
  updateTabCounts();
  window.applyCitationTabFilter?.();
}

// Clear category tabs (call when loading new document)
function resetCitationTabs() {
  byId('footnote-list')?.querySelectorAll('.card').forEach((card) => {
    delete card.dataset.citationCategory;
    card.style.display = '';
  });
  updateTabCounts();
  window.applyCitationTabFilter?.();
}

// Update tab counts based on list contents
function updateTabCounts() {
  const mainList = byId('footnote-list');
  const countAll = byId('count-all');
  const countVerified = byId('count-verified');
  const countIssues = byId('count-issues');
  const countUnresolved = byId('count-unresolved');

  const cards = Array.from(mainList?.querySelectorAll('.card') || []);
  const totals = cards.reduce((acc, card) => {
    const category = card.dataset.citationCategory;
    if (category === 'verified' || category === 'issues' || category === 'unresolved') {
      acc[category] += 1;
    }
    return acc;
  }, { verified: 0, issues: 0, unresolved: 0 });

  if (countAll) countAll.textContent = cards.length;
  if (countVerified) countVerified.textContent = totals.verified;
  if (countIssues) countIssues.textContent = totals.issues;
  if (countUnresolved) countUnresolved.textContent = totals.unresolved;
}

// Simple platform adapter for web app (no Tauri, no file system)
const createWebPlatform = (statusEl) => {
  return {
    setStatus: (text) => {
      if (statusEl) statusEl.textContent = text;
    },
    getDocumentPath: async () => null, // Web app: no document path
    setPdfPath: () => {}, // No-op for web app
  };
};

// Initialize authentication
(async () => {
  const loadingOverlay = byId('loading-overlay');
  if (loadingOverlay) loadingOverlay.style.display = 'flex';
  
  await initializeAuth(
    () => {
      // onSuccess
      showUserInfo();
    },
    () => {
      // onError
      showAuthForm();
    }
  );
})();

document.addEventListener('DOMContentLoaded', () => {
  // Make boot idempotent
  if (!window.__FC_WEB_APP_BOOTED__) {
    window.__FC_WEB_APP_BOOTED__ = true;

    const ac = new AbortController();
    const { signal } = ac;
    window.__FC_ABORT_CONTROLLER__ = ac;

    // Set up citation tabs
    setupCitationTabs();
    setupSidebarCollapse();
    setupWebPanes();
    setupStatusLine();

    (async () => {
      const statusEl = byId('status');
      const btnPick = byId('btn-select-pdf');
      const fileInput = byId('pdf-file-input');
      const btnParse = byId('btn-parse-pdf');
      const btnParseInline = byId('btn-parse-inline-toolbar');
      const btnVerifyAll = byId('btn-verify-all-toolbar');
      const btnGenerateReport = byId('btn-generate-report-toolbar');
      const btnGeneratePdfReport = byId('btn-generate-pdf-report-toolbar');
      const pickedNameEl = byId('selected-pdf-name');
      const footnoteListEl = byId('footnote-list');
      const btnPrev = byId('btn-prev-page');
      const btnNext = byId('btn-next-page');
      const pageJump = byId('page-jump');

      // Wait for authentication to complete
      console.log('[UI] Waiting for authentication to complete...');
      await getAuthReadyPromise();
      console.log('[UI] Authentication complete, setting up UI...');

      // Clear any cached API base
      sessionStorage.removeItem('fc.apiBase.resolved');

      // Resolve API base (will use Cloud Run)
      const apiBase = await resolveApiBase();
      console.log('[UI] API base resolved:', apiBase);

      // Hide loading overlay
      const loadingOverlay = byId('loading-overlay');
      if (loadingOverlay) loadingOverlay.style.display = 'none';

      // Initialize credit display and usage modal
      try {
        initCreditDisplay(apiBase, getAccessToken);
        initUsageModal(apiBase, getAccessToken);
        setupAccountPanes(apiBase, signal);
        console.log('[UI] Credit display and usage modal initialized');
      } catch (e) {
        console.warn('[UI] Failed to initialize credit display:', e);
      }

      // Apply the EU/ZDR Gemini-only restriction from the user's Firebase
      // custom claim, same as the Desktop App does at boot. This coerces any
      // stored non-Gemini model choices so getSettingsForApiPayload() (used by
      // interactive verifies and dashboard job submissions) only carries
      // Gemini models. The server enforces the same flag in the LLM gateway.
      (async () => {
        try {
          const restricted = await isZdrRestrictedUser(true);
          settingsManager.setProviderRestriction(restricted);
          if (restricted) {
            console.log('[Settings] EU-coded / ZDR account — models restricted to Gemini');
          }
        } catch (e) {
          console.warn('[Settings] Could not evaluate ZDR restriction:', e);
        }
      })();

      // Display current web-app version from hosted version.json (independent from API)
      try {
        const verEl = byId('app-version-display');
        if (verEl) {
          // Cache-bust to mitigate CDN lag
          const vresp = await fetch(`/version.json?ts=${Date.now()}`, { headers: { 'Accept': 'application/json' } });
          if (vresp.ok) {
            const vdata = await vresp.json();
            if (vdata?.latest) {
              verEl.textContent = `v${vdata.latest}`;
            } else {
              verEl.textContent = 'v?.?.?';
            }
          } else {
            verEl.textContent = 'v?.?.?';
          }
        }
      } catch (e) {
        console.warn('[UI] Failed to fetch hosted version.json:', e);
      }

      // Get client ID and set status
      const clientId = getAuthClientId();
      if (statusEl) {
        statusEl.textContent = `Connected to hosted API at ${apiBase} — client ${clientId.slice(0, 8)}`;
      }

      // Create platform adapter
      const platform = createWebPlatform(statusEl);

      // File list vs single file view
      const backToListBtn = byId('btn-back-to-file-list');
      let queueHelpers = null;
      const setFileView = (viewName) => {
        console.log('[UI] === VIEW SWITCH ===', viewName);
        document.body.classList.toggle('is-single-view', viewName === 'single');
        if (viewName === 'batch') {
          queueHelpers?.refreshActiveRowResults?.();
        }
        // When switching to single view, update toolbar buttons to reflect current mgr state
        if (viewName === 'single') {
          console.log('[UI] Switching to single view, mgr state:', {
            pages: mgr.pages?.length || 0,
            footnotes: mgr.footnotesCache?.length || 0,
            documentId: mgr.currentDocumentId || '(not set)'
          });
          // Use setTimeout to ensure all state updates have completed
          setTimeout(() => {
            if (typeof updateGenerateReportButton === 'function') updateGenerateReportButton();
            console.log('[UI] Single-file buttons updated after view switch');
          }, 100);
        }
      };

      backToListBtn?.addEventListener('click', () => setFileView('batch'), { signal });
      setFileView('batch');

      // Initialize footnotes manager (before queue UI so getMgr can reference it)
      let mgr = new FootnotesManager(apiBase, clientId, platform, getAccessToken);
      window.footcheckFootnotesManager = mgr;
      window.__footnotesManagerInstance = mgr;

      // Initialize queue UI using shared module
      const submitReportJob = async ({ file }) => {
        if (!file) {
          if (statusEl) statusEl.textContent = 'Choose a PDF before requesting an email report.';
          return;
        }

        const token = await getAccessToken();
        const form = new FormData();
        form.append('file', file, file.name || 'document.pdf');
        form.append('outputs', JSON.stringify({
          pdf: true,
          json: false,
          html: true,
          database: false,
        }));
        form.append('config', JSON.stringify({
          include_inline: true,
          internet_policy: 'smart',
          congruence_method: 'micro_tasks',
          concurrency: 4,
        }));
        const reportItems = (mgr.getReportItems?.() || []).map((fn) => {
          const footnoteId = String(fn.id);
          let latestResults = null;
          if (mgr.resultsCache && typeof mgr.resultsCache.getResults === 'function') {
            latestResults = mgr.resultsCache.getResults(footnoteId);
          }
          return {
            id: fn.id,
            reference_id: fn.reference_id,
            text: fn.text || '',
            context: fn.context || '',
            full_context: fn.full_context || '',
            page_context: fn.page_context || '',
            page_numbers: fn.page_numbers || [],
            cached_results: latestResults || fn.cached_results || {},
            cached_citations: fn.cached_citations || [],
            is_inline: fn.is_inline || false,
            parsed_citations: fn.parsed_citations || null,
            inline_citation: fn.inline_citation || null,
          };
        }).filter((fn) => fn.cached_results && Object.keys(fn.cached_results).length > 0);
        if (reportItems.length > 0) {
          form.append('footnotes', JSON.stringify(reportItems));
        }

        if (statusEl) statusEl.textContent = 'Queuing email report job...';
        const response = await fetch(`${apiBase}/report-jobs`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: form,
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload?.detail || `Report job failed (${response.status})`);
        }
        if (statusEl) {
          statusEl.textContent = 'Report job queued. We will email you when it is ready.';
        }
        return payload;
      };

      const dashboardEnabled = isDashboardEnabled() && !!byId('documents-dashboard');
      if (!dashboardEnabled) {
        // Legacy queue view (FC_WEB_DASHBOARD=off): shared queue-manager wiring.
        queueHelpers = initializeQueueUI({
          queueBody: byId('queue-table-body'),
          queueDropzone: byId('queue-dropzone'),
          btnAddRow: byId('btn-add-row'),
          btnQueueGo: byId('btn-queue-go'),
          btnQueueVerifyAll: byId('btn-queue-verify-all'),
          fileInput,
          folderInput: byId('data-folder-input'),
          pickedNameEl,
          signal,
          platform,
          btnParse,
          btnParseInline,
          btnVerifyAll,
          btnGeneratePdfReport,
          getMgr: () => mgr,
          onShowSingleView: () => setFileView('single'),
          onSubmitReportJob: submitReportJob,
          apiBase,
          getAccessToken
        });
      }
      mgr.setContainers({ footnoteListEl });
      
      // Initialize citation type filter panel in the sidebar
      // Try sidebar first, fall back to fn-pane for backwards compatibility
      const filterContainer = byId('sidebar-filters') || document.querySelector('#fn-pane');
      if (filterContainer) {
        mgr.initializeCitationTypeFilter(filterContainer.id === 'sidebar-filters' ? 'sidebar-filters' : 'fn-pane');
      }

      // Set up Generate HTML Report button (MUST be defined before setupFileUpload and setupPdfParsing)
      let updateGenerateReportButton = null;

      if (btnGenerateReport) {
        updateGenerateReportButton = () => {
          const hasPages = mgr.pages && mgr.pages.length > 0;
          const hasReportItems = (mgr.getReportItems?.() || mgr.footnotesCache || []).length > 0;
          btnGenerateReport.disabled = !(hasPages && hasReportItems);
          if (btnVerifyAll) btnVerifyAll.disabled = !hasReportItems;
          if (btnParseInline) {
            const inlineAlreadyParsed = btnParseInline.dataset.inlineParsed === 'true';
            btnParseInline.disabled = !hasPages || inlineAlreadyParsed;
            btnParseInline.title = inlineAlreadyParsed
              ? 'Inline citations have already been parsed for this document'
              : (!hasPages ? 'Parse the document first' : '');
          }
          // Annotated PDF report additionally needs the original PDF bytes.
          const pdfReady = hasPages && hasReportItems && mgr.canGeneratePdfReport?.();
          if (btnGeneratePdfReport) btnGeneratePdfReport.disabled = !pdfReady;
        };

        // Hook into manager methods to auto-update button state
        const originalLoadFromPdfBytes = mgr.loadFromPdfBytes.bind(mgr);
        mgr.loadFromPdfBytes = async (...args) => {
          const result = await originalLoadFromPdfBytes(...args);
          updateGenerateReportButton();
          return result;
        };

        const originalSetFootnotes = mgr.setFootnotes.bind(mgr);
        mgr.setFootnotes = function(...args) {
          const result = originalSetFootnotes(...args);
          updateGenerateReportButton();
          return result;
        };

        const originalSetItems = mgr.setItems.bind(mgr);
        mgr.setItems = function(...args) {
          const result = originalSetItems(...args);
          updateGenerateReportButton();
          return result;
        };

        // Initial state
        updateGenerateReportButton();
      }

      // Set up file upload
      const fileUpload = setupFileUpload({
        fileInput,
        btnSelectPdf: btnPick,
        pickedNameEl,
        btnParsePdf: btnParse,
        statusEl,
        getAccessToken,
        getClientId: getAuthClientId,
        apiBase,
        footnoteListEl,
        mgr,
        updateGenerateReportButton: updateGenerateReportButton,
        btnParseInline,
        btnVerifyAll
      });

      // Set up PDF parsing
      setupPdfParsing({
        btnParse,
        btnParseInline,
        btnVerifyAll,
        fileInput,
        statusEl,
        footnoteListEl,
        apiBase,
        getPicked: fileUpload.getPicked,
        getDocumentId: fileUpload.getDocumentId,
        mgr,
        signal,
        updateGenerateReportButton: updateGenerateReportButton
      });
      updateGenerateReportButton?.();

      // Open a saved (server-registry) document into the single-file view via
      // /documents/{id}/open-data. Used by the dashboard's Open action and by
      // ?document_id= links from the account page.
      const openSavedDocument = async (documentId) => {
        console.log('[UI] Opening saved document:', documentId);
        if (statusEl) statusEl.textContent = 'Opening saved document...';

        try {
          const openResponse = await fetch(`${apiBase}/documents/${encodeURIComponent(documentId)}/open-data`, {
            headers: {
              'Authorization': `Bearer ${await getAccessToken()}`
            },
            cache: 'no-store',
          });
          if (!openResponse.ok) {
            const errorText = await openResponse.text();
            if (openResponse.status === 202 || /ocr cache|ocr/i.test(errorText)) {
              if (statusEl) statusEl.textContent = 'OCR is still processing for this document. Please try opening it again when processing finishes.';
              return false;
            }
            throw new Error(`Failed to open saved document: ${openResponse.status}`);
          }

          const openData = await openResponse.json();
          fileUpload.setExistingDocument?.({
            id: openData.document_id || documentId,
            name: openData.document_name || openData.pdf_filename || 'Saved document'
          });

          await applyPdfParseResponse({
            responseData: openData,
            statusEl,
            footnoteListEl,
            apiBase,
            documentId: openData.document_id || documentId,
            mgr,
            updateGenerateReportButton,
            btnParseInline,
            btnVerifyAll
          });

          // Saved documents open straight into the single-file view (they have
          // no dashboard/queue-held file); without this the user lands on an
          // empty list view.
          setFileView('single');
          return true;
        } catch (err) {
          console.error('[UI] Error opening saved document:', err);
          if (statusEl) statusEl.textContent = `Error opening saved document: ${err.message}`;
          return false;
        }
      };

      // Documents dashboard (plan 2A): server-jobs-native landing view.
      if (dashboardEnabled) {
        const uploadSection = byId('upload-section');
        if (uploadSection) uploadSection.style.display = 'none';
        const dashboardEl = byId('documents-dashboard');
        if (dashboardEl) dashboardEl.style.display = 'flex';

        const documentsStore = new DocumentsStore({ apiBase, getAccessToken, signal });
        initializeDashboard({
          root: dashboardEl,
          activeContainer: byId('dashboard-active-cards'),
          historyContainer: byId('dashboard-history-list'),
          historyHeader: byId('dashboard-history-header'),
          searchInput: byId('dashboard-search'),
          dropzone: byId('dashboard-dropzone'),
          fileInput: byId('dashboard-file-input'),
          emailToggle: byId('dashboard-email-toggle'),
          verifyAllBtn: byId('btn-dashboard-verify-all'),
          refreshBtn: byId('btn-dashboard-refresh'),
          store: documentsStore,
          onOpenDocument: async (documentId, entry) => {
            // A dashboard upload already has the browser File object, so it
            // can be parsed and inspected before the user verifies it. Saved
            // entries without a local file continue through open-data.
            try {
              if (entry?.file && (entry.upload || !entry.inRegistry)) {
                setFileView('single');
                await fileUpload.setFile(entry.file);
                return;
              }
              await openSavedDocument(documentId);
            } catch (err) {
              console.error('[UI] Error opening dashboard document:', err);
              setFileView('batch');
              if (statusEl) statusEl.textContent = `Error opening document: ${err.message || err}`;
            }
          },
          setStatus: (text) => { if (statusEl) statusEl.textContent = text; },
          signal,
        });
        window.__FC_DOCUMENTS_STORE__ = documentsStore;
      }

      // Check for URL parameters from landing page redirect
      const urlParams = new URLSearchParams(window.location.search);
      const sourceType = urlParams.get('source');
      const pdfUrl = urlParams.get('url');
      const pdfName = urlParams.get('name');
      const savedDocumentId = urlParams.get('document_id');

      if (savedDocumentId) {
        const opened = await openSavedDocument(savedDocumentId);
        if (opened) {
          window.history.replaceState({}, document.title, window.location.pathname);
        } else if (footnoteListEl && !document.body.classList.contains('is-single-view')) {
          footnoteListEl.innerHTML = `<li>OCR pending for this document... ⏳</li>`;
        }
      } else if (pdfUrl && (sourceType === 'upload' || sourceType === 'url')) {
        console.log('[UI] Loading PDF from URL parameter:', pdfUrl);
        if (statusEl) statusEl.textContent = 'Loading PDF from URL...';
        
        try {
          // Firebase Storage URLs are CORS-enabled and can be fetched directly.
          // External URLs (arbitrary sites) usually aren't, so route them through
          // the authenticated server-side proxy to avoid browser CORS failures.
          const isStorageUrl = /(?:firebasestorage|storage)\.googleapis\.com/.test(pdfUrl);
          let response;
          if (isStorageUrl) {
            response = await fetch(pdfUrl);
          } else {
            const token = await getAccessToken();
            response = await fetch(`${apiBase}/web/proxy-pdf?url=${encodeURIComponent(pdfUrl)}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
          }
          if (!response.ok) throw new Error(`Failed to fetch PDF: ${response.status}`);

          const blob = await response.blob();
          const filename = pdfName || 'document.pdf';
          const file = new File([blob], decodeURIComponent(filename), { type: 'application/pdf' });
          
          // Set the file in the upload handler (handles UI updates and auto-parse)
          if (fileUpload.setFile) {
            await fileUpload.setFile(file);
          }

          // Landing uploads/URLs load a single document (no queue card); show the
          // single-file view so the user doesn't land on an empty batch view.
          setFileView('single');

          // Clear URL parameters to avoid reload issues
          window.history.replaceState({}, document.title, window.location.pathname);
        } catch (err) {
          console.error('[UI] Error loading PDF from URL:', err);
          // Provide helpful error message for CORS issues
          if (err.message.includes('Failed to fetch') || err.name === 'TypeError') {
            if (statusEl) statusEl.textContent = 'Cannot load external PDF (CORS). Please download the file and upload it directly.';
          } else {
            if (statusEl) statusEl.textContent = `Error loading PDF: ${err.message}`;
          }
          // Clear URL parameters
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      }

      // Page navigation
      const scrollToPage = (n) => {
        const el = document.getElementById(`page-${n}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      };

      pageJump?.addEventListener('change', () => {
        const n = Number(pageJump.value || '1');
        if (Number.isFinite(n) && n > 0) scrollToPage(n);
      }, { signal });

      btnPrev?.addEventListener('click', () => {
        const n = Math.max(1, Number(pageJump?.value || '1') - 1);
        if (pageJump) pageJump.value = String(n);
        scrollToPage(n);
      }, { signal });

      btnNext?.addEventListener('click', () => {
        const total = (mgr.pages?.length || 1);
        const n = Math.min(total, Number(pageJump?.value || '1') + 1);
        if (pageJump) pageJump.value = String(n);
        scrollToPage(n);
      }, { signal });

      // Generate HTML report button handler
      btnGenerateReport?.addEventListener('click', async (e) => {
        e.preventDefault();
        if (!mgr.pages || mgr.pages.length === 0) {
          if (statusEl) statusEl.textContent = 'Please parse PDF first.';
          return;
        }
        
        try {
          btnGenerateReport.disabled = true;
          if (statusEl) statusEl.textContent = 'Generating HTML report...';
          
          const pdfName = fileUpload.getPicked()?.name || 'document.pdf';
          const blob = await mgr.generateHtmlReport(pdfName);
          
          // Get current user ID from Firebase Auth
          const authInstance = getAuthInstance();
          const currentUser = authInstance.currentUser;
          if (!currentUser) {
            throw new Error('User not authenticated. Please sign in.');
          }
          const userId = currentUser.uid;
          
          // Get document ID if available
          const documentId = fileUpload.getDocumentId();
          
          // Generate filename for the report
          const baseName = pdfName.replace(/\.[^/.]+$/, '') || 'document';
          const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
          const filename = `${baseName}_citation_report_${timestamp}.html`;
          
          // Upload to Firebase Storage
          if (statusEl) statusEl.textContent = 'Uploading report to Firebase...';
          const publicUrl = await uploadHtmlReport(blob, filename, userId, documentId);
          
          // Register the report in user's documents registry
          try {
            const accessToken = await getAccessToken();
            if (accessToken && documentId) {
              const apiBase = await resolveApiBase();
              
              // Count verified/unverified citations from current state
              let citationsChecked = 0;
              let citationsVerified = 0;
              let citationsUnverified = 0;
              
              // Try to get citation stats from the footnotes manager
              if (window.__footnotesManagerInstance) {
                const stats = window.__footnotesManagerInstance.getCitationStats?.();
                if (stats) {
                  citationsChecked = stats.total || 0;
                  citationsVerified = stats.verified || 0;
                  citationsUnverified = stats.unverified || 0;
                }
              }
              
              await fetch(`${apiBase}/documents/reports`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                  document_id: documentId,
                  report_name: filename,
                  report_url: publicUrl,
                  report_type: 'html',
                  citations_checked: citationsChecked,
                  citations_verified: citationsVerified,
                  citations_unverified: citationsUnverified,
                }),
              });
              console.log('[REPORT] Registered report in documents registry');
            }
          } catch (regErr) {
            // Non-fatal - report is uploaded, just not registered
            console.warn('[REPORT] Failed to register report (non-fatal):', regErr);
          }
          
          // Show success message and redirect
          if (statusEl) statusEl.textContent = 'Report uploaded successfully! Opening...';
          
          // Ask user if they want to open in new tab or copy link
          const action = window.confirm(
            'HTML report uploaded successfully!\n\n' +
            'OK = Open report in new tab\n' +
            'Cancel = Copy link to clipboard'
          );
          
          if (action) {
            // Open in new tab
            const previewWindow = window.open(publicUrl, '_blank');
            if (previewWindow) {
              if (statusEl) statusEl.textContent = 'Report opened in new tab.';
            } else {
              // Popup blocked, copy link instead
              await navigator.clipboard.writeText(publicUrl);
              if (statusEl) statusEl.textContent = 'Report link copied to clipboard (popup blocked).';
              alert(`Report link copied to clipboard:\n${publicUrl}`);
            }
          } else {
            // Copy link to clipboard
            try {
              await navigator.clipboard.writeText(publicUrl);
              if (statusEl) statusEl.textContent = 'Report link copied to clipboard.';
              alert(`Report link copied to clipboard:\n${publicUrl}`);
            } catch (clipboardErr) {
              // Fallback: show the URL in an alert
              if (statusEl) statusEl.textContent = 'Report uploaded.';
              alert(`Report URL:\n${publicUrl}\n\n(Copy this URL manually)`);
            }
          }
        } catch (err) {
          console.error('Failed to generate or upload HTML report:', err);
          if (statusEl) statusEl.textContent = `Error: ${err.message || err}`;
          alert(`Failed to generate or upload report: ${err.message || err}`);
        } finally {
          if (updateGenerateReportButton) {
            updateGenerateReportButton();
          } else {
            btnGenerateReport.disabled = false;
          }
        }
      }, { signal });

      // Generate annotated PDF report button handler
      btnGeneratePdfReport?.addEventListener('click', async (e) => {
        e.preventDefault();
        if (!mgr.canGeneratePdfReport?.()) {
          if (statusEl) statusEl.textContent = 'Original PDF not available for annotation. Please re-open the file.';
          return;
        }

        try {
          btnGeneratePdfReport.disabled = true;
          if (statusEl) statusEl.textContent = 'Generating annotated PDF report...';

          const pdfName = fileUpload.getPicked()?.name || 'document.pdf';
          const blob = await mgr.generatePdfReport(pdfName);

          const authInstance = getAuthInstance();
          const currentUser = authInstance.currentUser;
          if (!currentUser) {
            throw new Error('User not authenticated. Please sign in.');
          }
          const userId = currentUser.uid;
          const documentId = fileUpload.getDocumentId();

          const baseName = pdfName.replace(/\.[^/.]+$/, '') || 'document';
          const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
          const filename = `${baseName}_citation_report_${timestamp}.pdf`;

          if (statusEl) statusEl.textContent = 'Uploading PDF report to Firebase...';
          const publicUrl = await uploadHtmlReport(blob, filename, userId, documentId, 'application/pdf');

          // Register the report in the user's documents registry (non-fatal).
          try {
            const accessToken = await getAccessToken();
            if (accessToken && documentId) {
              const apiBase = await resolveApiBase();

              let citationsChecked = 0;
              let citationsVerified = 0;
              let citationsUnverified = 0;
              if (window.__footnotesManagerInstance) {
                const stats = window.__footnotesManagerInstance.getCitationStats?.();
                if (stats) {
                  citationsChecked = stats.total || 0;
                  citationsVerified = stats.verified || 0;
                  citationsUnverified = stats.unverified || 0;
                }
              }

              await fetch(`${apiBase}/documents/reports`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                  document_id: documentId,
                  report_name: filename,
                  report_url: publicUrl,
                  report_type: 'pdf',
                  citations_checked: citationsChecked,
                  citations_verified: citationsVerified,
                  citations_unverified: citationsUnverified,
                }),
              });
              console.log('[REPORT] Registered PDF report in documents registry');
            }
          } catch (regErr) {
            console.warn('[REPORT] Failed to register PDF report (non-fatal):', regErr);
          }

          if (statusEl) statusEl.textContent = 'PDF report uploaded! Opening...';
          const action = window.confirm(
            'Annotated PDF report uploaded successfully!\n\n' +
            'OK = Open report in new tab\n' +
            'Cancel = Copy link to clipboard'
          );

          if (action) {
            const previewWindow = window.open(publicUrl, '_blank');
            if (previewWindow) {
              if (statusEl) statusEl.textContent = 'PDF report opened in new tab.';
            } else {
              await navigator.clipboard.writeText(publicUrl);
              if (statusEl) statusEl.textContent = 'PDF report link copied (popup blocked).';
              alert(`Report link copied to clipboard:\n${publicUrl}`);
            }
          } else {
            try {
              await navigator.clipboard.writeText(publicUrl);
              if (statusEl) statusEl.textContent = 'PDF report link copied to clipboard.';
              alert(`Report link copied to clipboard:\n${publicUrl}`);
            } catch (clipboardErr) {
              if (statusEl) statusEl.textContent = 'PDF report uploaded.';
              alert(`Report URL:\n${publicUrl}\n\n(Copy this URL manually)`);
            }
          }
        } catch (err) {
          console.error('Failed to generate or upload PDF report:', err);
          if (statusEl) statusEl.textContent = `Error: ${err.message || err}`;
          alert(`Failed to generate or upload PDF report: ${err.message || err}`);
        } finally {
          if (updateGenerateReportButton) {
            updateGenerateReportButton();
          } else {
            btnGeneratePdfReport.disabled = false;
          }
        }
      }, { signal });

      platform.setStatus?.('Idle');
    })();

    // HMR cleanup
    if (typeof module !== 'undefined' && module.hot) {
      module.hot.dispose(() => {
        try { ac.abort(); } catch {}
        window.__FC_WEB_APP_BOOTED__ = false;
      });
    }
  }
});
